import { Search, Plus, Calendar, Loader2, AlertCircle, ChevronDown } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { notificationsSocketService } from "@/modules/dashboard/notifications/services/notifications-socket.service";
import { Button } from "@/shared/components/form/Button";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { DeleteNoteModal } from "../components/DeleteNoteModal";
import { NewNoteModal } from "../components/NewNoteModal";
import { NewsCard } from "../components/NewsCard";
import { NOTES_PAGE_SIZE } from "../constants/news.constants";
import { notesService } from "../services/notes.service";
import type { NewsNote } from "../types/news.types";
import type { CreateNoteRequest } from "../types/notes-api.types";
import { mapNoteItemsToNewsNotes } from "../utils/mapNoteFromApi";
import { canCreateNote, canDeleteNote } from "../utils/notePermissions";

const SEARCH_DEBOUNCE_MS = 400;

export const NewsPage = () => {
  const { t } = useTranslations("news");
  const { t: tGlobal } = useTranslations("global");
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_gestion") },
      { label: tGlobal("sidebar_menu_news"), to: "/app/news" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);
  const { user } = useAuth();
  const [hasNoAccess, setHasNoAccess] = useState(false);
  const [search, setSearch] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [notes, setNotes] = useState<NewsNote[]>([]);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchOverlayKeep, setSearchOverlayKeep] = useState(false);
  const [newNoteModalOpen, setNewNoteModalOpen] = useState(false);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<NewsNote | null>(null);
  const [isDeletingNote, setIsDeletingNote] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const overlayKeepRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const today = new Date().toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const fetchNotes = useCallback(
    async (skipOffset: number, append: boolean, searchTerm: string) => {
      const isLoadMore = append && skipOffset > 0;
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setSearchOverlayKeep(false);
      }
      setError(null);
      setHasNoAccess(false);
      try {
        const response = await notesService.fetchList({
          skip: skipOffset,
          limit: NOTES_PAGE_SIZE,
          status: "ACTIVE",
          search: searchTerm.trim() || undefined,
        });
        const mapped = mapNoteItemsToNewsNotes(response.data);
        if (append) {
          setNotes((prev) => [...prev, ...mapped]);
        } else {
          setNotes(mapped);
        }
        setHasMore(response.hasMore ?? false);
        setSkip(skipOffset + response.data.length);
      } catch (err) {
        if (HttpApiService.isAccessDeniedError(err)) {
          setHasNoAccess(true);
          if (!append) setNotes([]);
          return;
        }
        const message =
          err && typeof err === "object" && "message" in err
            ? String((err as { message: unknown }).message)
            : t("error_load");
        setError(message);
        if (!append) setNotes([]);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
        if (!append && searchTerm.trim() !== "") {
          if (overlayKeepRef.current) clearTimeout(overlayKeepRef.current);
          setSearchOverlayKeep(true);
          overlayKeepRef.current = setTimeout(() => {
            setSearchOverlayKeep(false);
            overlayKeepRef.current = null;
          }, 350);
        }
      }
    },
    [t],
  );

  useEffect(() => {
    fetchNotes(0, false, searchParam);
  }, [searchParam, fetchNotes]);

  useEffect(() => {
    const unsubscribe = notificationsSocketService.onNotification((notification) => {
      if (notification.type === "NOTE_CREATED") {
        fetchNotes(0, false, searchParam);
      }
    });
    return unsubscribe;
  }, [fetchNotes, searchParam]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchParam(search);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [search]);

  useEffect(() => {
    return () => {
      if (overlayKeepRef.current) clearTimeout(overlayKeepRef.current);
    };
  }, []);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoadingMore) return;
    fetchNotes(skip, true, searchParam);
  }, [hasMore, isLoadingMore, skip, searchParam, fetchNotes]);

  const handleCreateNote = useCallback(
    async (data: CreateNoteRequest, attachments: File[]) => {
      setIsCreatingNote(true);
      try {
        await notesService.create(data, attachments);
        setNewNoteModalOpen(false);
        await fetchNotes(0, false, searchParam);
      } catch (err) {
        if (HttpApiService.isAccessDeniedError(err)) {
          setHasNoAccess(true);
          setNewNoteModalOpen(false);
          return;
        }
        const message =
          err && typeof err === "object" && "message" in err
            ? String((err as { message: unknown }).message)
            : t("error_create");
        setError(message);
      } finally {
        setIsCreatingNote(false);
      }
    },
    [searchParam, fetchNotes, t],
  );

  const handleConfirmDeleteNote = useCallback(async () => {
    if (!noteToDelete) return;
    setIsDeletingNote(true);
    try {
      await notesService.delete(noteToDelete.id);
      setNoteToDelete(null);
      await fetchNotes(0, false, searchParam);
    } catch (err) {
      if (HttpApiService.isAccessDeniedError(err)) {
        setHasNoAccess(true);
        setNoteToDelete(null);
        return;
      }
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : t("error_delete");
      setError(message);
    } finally {
      setIsDeletingNote(false);
    }
  }, [noteToDelete, searchParam, fetchNotes, t]);

  if (hasNoAccess) {
    return <NoAccessView />;
  }

  return (
    <div className="flex flex-col min-h-0 flex-1 min-w-0">
      <header className="h-16 border-b border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 flex items-center justify-between px-4 md:px-8 sticky top-0 z-10 shrink-0">
        <div className="flex-1 max-w-lg">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("search_placeholder")}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-zinc-800 border-0 rounded-xl focus:ring-2 focus:ring-green-500 text-sm text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400"
              aria-label={t("search_aria")}
            />
          </div>
        </div>
        <div className="flex items-center gap-3 ml-4">
          {canCreateNote(user) && (
            <Button
              type="button"
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              className="shrink-0"
              onClick={() => {
                setError(null);
                setNewNoteModalOpen(true);
              }}
            >
              {t("new_note_button")}
            </Button>
          )}
        </div>
      </header>

      <div className="p-4 md:p-8 overflow-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">{t("page_title")}</h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">{t("page_subtitle")}</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 shrink-0">
            <Calendar className="h-4 w-4" />
            <span>{today}</span>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {isLoading && notes.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 py-16">
            <Loader2 className="h-10 w-10 animate-spin text-green-500" />
            <p className="text-sm text-slate-500 dark:text-slate-400">{t("loading_notes")}</p>
          </div>
        ) : (
          <>
            <div className="relative">
              <div
                className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 transition-opacity duration-500 ease-out ${
                  (isLoading && notes.length > 0) || searchOverlayKeep
                    ? "opacity-50 pointer-events-none"
                    : "opacity-100"
                }`}
              >
                {notes.map((note) => (
                  <NewsCard
                    key={note.id}
                    note={note}
                    canDelete={canDeleteNote(user, note)}
                    onDelete={(n) => setNoteToDelete(n)}
                  />
                ))}
              </div>
              {((isLoading && notes.length > 0) || searchOverlayKeep) && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl bg-white/40 dark:bg-zinc-900/40 backdrop-blur-[3px] min-h-[200px] transition-opacity duration-500 ease-out"
                  aria-live="polite"
                  aria-busy={isLoading}
                >
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t("searching")}</p>
                </div>
              )}
            </div>

            {notes.length === 0 && !isLoading && (
              <div className="rounded-xl border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 p-8 text-center">
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {searchParam.trim() ? t("empty_search") : t("empty_no_notes")}
                </p>
              </div>
            )}

            {hasMore && (
              <div className="flex justify-center pt-2 pb-4 mt-8">
                <button
                  type="button"
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="flex items-center gap-2 text-base font-semibold text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 disabled:opacity-50 transition-all py-2.5 px-4 rounded-lg hover:bg-green-500/10 dark:hover:bg-green-500/20 border border-transparent hover:border-green-500/30"
                  aria-label={t("load_more_aria")}
                >
                  {isLoadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                      {t("load_more_loading")}
                    </>
                  ) : (
                    <>
                      {t("load_more")}
                      <ChevronDown className="h-5 w-5 shrink-0" />
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <NewNoteModal
        isOpen={newNoteModalOpen}
        onClose={() => setNewNoteModalOpen(false)}
        onSubmit={handleCreateNote}
        isLoading={isCreatingNote}
      />

      <DeleteNoteModal
        isOpen={noteToDelete !== null}
        onClose={() => setNoteToDelete(null)}
        onConfirm={handleConfirmDeleteNote}
        noteTitle={noteToDelete?.title}
        isLoading={isDeletingNote}
      />
    </div>
  );
};
